import { useState, useRef, useEffect } from 'react';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, User, LogIn, LogOut, ChevronDown, Shield, MessageSquare, Store, Sparkles, CreditCard, Heart, CalendarDays } from 'lucide-react';
import { Logo } from '@/components/shared/Logo';
import { useAuthStore } from '@/stores/auth-store';
import { useAuth } from '@/hooks/use-auth';

const NAV_LINKS = [
  { to: '/', label: 'Accueil' },
  { to: '/directory', label: 'Festivals' },
  { to: '/exhibitors', label: 'Exposants' },
  { to: '/dashboard', label: 'Dashboard' },
  { to: '/pricing', label: 'Tarifs' },
  { to: '/docs', label: 'Documentation' },
];

/*
 * @id     fest.layoutPlatform
 * @do     encadrer_plateforme
 * @role   ui
 * @layer  ui
 * @human  Mise en page générale de la plateforme (hors festival).
 */
export function PlatformLayout() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { isAuthenticated, profile } = useAuthStore();
  const { signOut } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const userMenuRef = useRef<HTMLDivElement>(null);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  // Close user menu on click outside
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSignOut = async () => {
    await signOut();
    setUserMenuOpen(false);
    setMobileMenuOpen(false);
    navigate('/');
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      {/* Top Navbar */}
      <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          {/* Logo */}
          <Link to="/" className="flex-shrink-0">
            <Logo />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden items-center gap-1 md:flex">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                  isActive(link.to)
                    ? 'bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                }`}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          {/* Desktop Auth */}
          <div className="hidden items-center gap-2 md:flex">
            {isAuthenticated ? (
              <div className="relative" ref={userMenuRef}>
                <button
                  type="button"
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
                >
                  <User className="h-4 w-4" />
                  {profile?.display_name || profile?.username || 'Mon compte'}
                  <ChevronDown className="h-3 w-3" />
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 mt-1 w-48 rounded-md border border-border bg-card py-1 shadow-lg">
                    <div className="border-b border-border px-3 py-2">
                      <p className="text-sm font-medium text-foreground">{profile?.display_name || profile?.username}</p>
                      <p className="text-xs text-muted-foreground">{profile?.email}</p>
                    </div>
                    <Link
                      to="/profile"
                      onClick={() => setUserMenuOpen(false)}
                      className="flex w-full items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-accent"
                    >
                      <User className="h-4 w-4" />
                      Mon profil
                    </Link>
                    <div className="border-t border-border my-1" />
                    <Link to="/visitor" onClick={() => setUserMenuOpen(false)} className="flex w-full items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-accent">
                      <Sparkles className="h-4 w-4" /> Espace visiteur
                    </Link>
                    <Link to="/volunteer" onClick={() => setUserMenuOpen(false)} className="flex w-full items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-accent">
                      <Heart className="h-4 w-4" /> Espace benevole
                    </Link>
                    <Link to="/exhibitor" onClick={() => setUserMenuOpen(false)} className="flex w-full items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-accent">
                      <Store className="h-4 w-4" /> Espace exposant
                    </Link>
                    {(profile?.platform_role === 'organizer' || profile?.platform_role === 'admin') && (
                      <Link to="/organizer" onClick={() => setUserMenuOpen(false)} className="flex w-full items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-accent">
                        <CalendarDays className="h-4 w-4" /> Espace organisateur
                      </Link>
                    )}
                    <div className="border-t border-border my-1" />
                    <Link to="/messaging" onClick={() => setUserMenuOpen(false)} className="flex w-full items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-accent">
                      <MessageSquare className="h-4 w-4" /> Messagerie
                    </Link>
                    {(profile?.platform_role === 'organizer' || profile?.platform_role === 'admin') && (
                      <Link to="/billing" onClick={() => setUserMenuOpen(false)} className="flex w-full items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-accent">
                        <CreditCard className="h-4 w-4" /> Facturation
                      </Link>
                    )}
                    {profile?.platform_role === 'admin' && (
                      <Link to="/admin" onClick={() => setUserMenuOpen(false)} className="flex w-full items-center gap-2 px-3 py-2 text-sm text-foreground hover:bg-accent">
                        <Shield className="h-4 w-4" /> Administration
                      </Link>
                    )}
                    <button
                      type="button"
                      onClick={handleSignOut}
                      className="flex w-full items-center gap-2 px-3 py-2 text-sm text-destructive hover:bg-accent"
                    >
                      <LogOut className="h-4 w-4" />
                      Se deconnecter
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link
                  to="/login"
                  className="inline-flex items-center gap-2 rounded-md px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
                >
                  <LogIn className="h-4 w-4" />
                  Connexion
                </Link>
                <Link
                  to="/signup"
                  className="inline-flex items-center gap-2 rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
                >
                  S'inscrire
                </Link>
              </>
            )}
          </div>

          {/* Mobile Hamburger */}
          <button
            type="button"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="inline-flex items-center justify-center rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-foreground md:hidden"
            aria-label="Ouvrir le menu"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="border-t border-border md:hidden">
            <nav className="flex flex-col gap-1 px-4 py-3">
              {NAV_LINKS.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${
                    isActive(link.to)
                      ? 'bg-primary/10 text-primary'
                      : 'text-muted-foreground hover:bg-accent hover:text-foreground'
                  }`}
                >
                  {link.label}
                </Link>
              ))}
              <div className="mt-2 border-t border-border pt-2">
                {isAuthenticated ? (
                  <>
                    <Link
                      to="/profile"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-foreground hover:bg-accent"
                    >
                      <User className="h-4 w-4" />
                      Mon profil
                    </Link>
                    <Link to="/visitor" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-foreground hover:bg-accent">
                      <Sparkles className="h-4 w-4" /> Espace visiteur
                    </Link>
                    <Link to="/volunteer" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-foreground hover:bg-accent">
                      <Heart className="h-4 w-4" /> Espace benevole
                    </Link>
                    <Link to="/exhibitor" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-foreground hover:bg-accent">
                      <Store className="h-4 w-4" /> Espace exposant
                    </Link>
                    {(profile?.platform_role === 'organizer' || profile?.platform_role === 'admin') && (
                      <Link to="/organizer" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-foreground hover:bg-accent">
                        <CalendarDays className="h-4 w-4" /> Espace organisateur
                      </Link>
                    )}
                    <Link to="/messaging" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-foreground hover:bg-accent">
                      <MessageSquare className="h-4 w-4" /> Messagerie
                    </Link>
                    {(profile?.platform_role === 'organizer' || profile?.platform_role === 'admin') && (
                      <Link to="/billing" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-foreground hover:bg-accent">
                        <CreditCard className="h-4 w-4" /> Facturation
                      </Link>
                    )}
                    {profile?.platform_role === 'admin' && (
                      <Link to="/admin" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-foreground hover:bg-accent">
                        <Shield className="h-4 w-4" /> Administration
                      </Link>
                    )}
                    <button
                      type="button"
                      onClick={handleSignOut}
                      className="flex w-full items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-destructive hover:bg-accent"
                    >
                      <LogOut className="h-4 w-4" />
                      Se deconnecter
                    </button>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-foreground hover:bg-accent"
                    >
                      <LogIn className="h-4 w-4" />
                      Se connecter
                    </Link>
                    <Link
                      to="/signup"
                      onClick={() => setMobileMenuOpen(false)}
                      className="flex items-center gap-2 rounded-md px-3 py-2 text-sm font-medium text-primary hover:bg-accent"
                    >
                      S'inscrire
                    </Link>
                  </>
                )}
              </div>
            </nav>
          </div>
        )}
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="border-t border-border bg-background">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
            <span>&copy; 2026 Festosh</span>
            <Link to="/about" className="hover:text-foreground hover:underline">A propos</Link>
            <Link to="/docs" className="hover:text-foreground hover:underline">Documentation</Link>
            <Link to="/privacy" className="hover:text-foreground hover:underline">Confidentialite</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
